import { useEffect, useRef } from 'react';

import { initiateLogin, storeReturnPath, verifySession } from '@/modules/auth';
import { useAuthStore } from '@/stores/auth-store';

/**
 * ProtectedRoute guard component.
 *
 * Wraps protected content and enforces authentication:
 * 1. On mount, if auth status is 'loading', calls verifySession() and shows a loading skeleton
 * 2. If status is 'authenticated', renders children
 * 3. If status is 'unauthenticated' or 'error', stores the current path in sessionStorage
 *    and initiates the login redirect
 * 4. Shows a loading skeleton (pulsing placeholder) during verification
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const error = useAuthStore((state) => state.error);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    if (status === 'loading' && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifySession().catch(() => {
        // verifySession updates the store on failure (setError or setUnauthenticated)
        // handled by the store state change triggering re-render
      });
    }
  }, [status]);

  useEffect(() => {
    if (status === 'unauthenticated' || status === 'error') {
      const currentPath = window.location.pathname + window.location.search;
      storeReturnPath(currentPath);
      initiateLogin().catch(() => {
        // If initiateLogin fails (e.g., network error fetching config),
        // the user stays on the loading skeleton. The error state in the store
        // will be handled by the error UI below on next render cycle.
      });
    }
  }, [status]);

  if (status === 'loading') {
    return <LoadingSkeleton />;
  }

  if (status === 'unauthenticated' || status === 'error') {
    // Show loading skeleton while redirect is in progress
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-border border-t-brand-600"
            aria-hidden="true"
          />
          <p className="text-sm text-content-secondary">
            {error ? 'Redirecting to login...' : 'Redirecting to login...'}
          </p>
        </div>
      </div>
    );
  }

  // status === 'authenticated'
  return <>{children}</>;
}

/**
 * Loading skeleton displayed during session verification.
 * Renders a pulsing placeholder that mimics a page layout.
 */
function LoadingSkeleton() {
  return (
    <div
      className="flex min-h-screen flex-col"
      role="status"
      aria-live="polite"
      aria-label="Verifying session"
    >
      {/* Header skeleton */}
      <div className="h-16 w-full animate-pulse bg-surface-raised" />

      <div className="flex flex-1">
        {/* Sidebar skeleton */}
        <div className="hidden w-64 animate-pulse bg-surface lg:block" />

        {/* Main content skeleton */}
        <div className="flex-1 p-6">
          <div className="space-y-4">
            {/* Title skeleton */}
            <div className="h-8 w-1/3 animate-pulse rounded bg-surface-raised" />
            {/* Content block skeletons */}
            <div className="h-4 w-2/3 animate-pulse rounded bg-surface-raised" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-surface-raised" />
            <div className="mt-6 h-32 w-full animate-pulse rounded bg-surface-raised" />
            <div className="h-32 w-full animate-pulse rounded bg-surface-raised" />
          </div>
        </div>
      </div>
    </div>
  );
}
